import React from 'react';
import Typography from 'carbon-react/lib/components/typography';
import Icon from 'carbon-react/lib/components/icon';

import * as styles from './performance-overview.scss';

interface PerformanceMetricCardProps {
  label: string;
  value: string;
  variance?: string;
  varianceValue?: string;
  varianceSuffix?: string;
  isPositiveVariance?: boolean;
  isLoading?: boolean;
  valueColor?: string;
  style?: React.CSSProperties;
}

const getVarianceClass = (
  isZero: boolean,
  isPositive?: boolean
): string | undefined => {
  if (isZero) {
    return undefined;
  }
  return isPositive ? styles.positiveVariance : styles.negativeVariance;
};

const VarianceIndicator = ({
  varianceValue,
  varianceSuffix,
  varianceClass,
  isUp
}: {
  varianceValue?: string;
  varianceSuffix?: string;
  varianceClass?: string;
  isUp: boolean;
}) => (
  <div className={styles.cardVariance}>
    <span className={varianceClass}>
      <Icon type={isUp ? 'arrow_up' : 'arrow_down'} fontSize="small" />
      <span className={styles.varianceValueText}>{varianceValue}</span>
    </span>
    {varianceSuffix && <span className={styles.varianceSuffixText}>{varianceSuffix}</span>}
  </div>
);

export const PerformanceMetricCard = ({
  label,
  value,
  variance,
  varianceValue,
  varianceSuffix,
  isPositiveVariance,
  isLoading,
  valueColor,
  style
}: PerformanceMetricCardProps) => {
  const hasVariance = variance !== undefined && variance !== null;
  const varianceNum = hasVariance ? Number(variance) : 0;
  const isUp = varianceNum > 0;
  const isZero = varianceNum === 0;
  const varianceClass = getVarianceClass(isZero, isPositiveVariance);

  return (
    <div className={styles.metricCard} style={style}>
      <Typography fontSize="14px" fontWeight="500" lineHeight="150%" mb={0}>
        {label}
      </Typography>
      {isLoading ? (
        <>
          <div className={styles.skeletonValue} />
          {hasVariance && <div className={styles.skeletonVariance} />}
        </>
      ) : (
        <>
          <Typography fontSize="24px" fontWeight="700" lineHeight="125%" mb={0} color={valueColor}>
            {value}
          </Typography>
          {hasVariance && !isZero && (
            <VarianceIndicator
              varianceValue={varianceValue}
              varianceSuffix={varianceSuffix}
              varianceClass={varianceClass}
              isUp={isUp}
            />
          )}
        </>
      )}
    </div>
  );
};
